import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { ReactComponent as ScraperAPIIllustration3 } from "assets/images/scraperapi-illustration3.svg";

import { fmtDate, fmtNumber } from "utils";
import { isEnterprise, isLegacyAnnualPlan, getPlanCategory } from "utils/planUtils";

import { useApiCalls } from "providers/ApiCallsProvider";

import StatefulLink from "components/StatefulLink";
import TaggedText from "components/TaggedText";
import Spinner from "components/Spinner";

import { useChargebeePortals } from "hooks/useChargebeePortals";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";

import { useUser } from "routes/dataroutes/UserData";
import { isGrandfatheredPlan } from "utils/legacyRate";
import { LegacyPricingNote } from "components/LegacyRateCallout";
import { PricingOptionsCard } from "routes/billing/PricingOptionsCard";

import ActiveSubscription from "types/ActiveSubscription";

import CancelSubscriptionLinks from "./CancelSubscriptionLinks";
import { useCounters } from "v2/hooks/billing/useCounters";


export default function SubscriptionManagementCard(
  {
    setApiCallInProgress
  }: {
    setApiCallInProgress?: () => void;
  }
) {
  const user = useUser()!;
  const { counters } = useCounters();
  const { activeSubscription } = useActiveSubscription();
  const { openChargebeePortal, isChargebeeReady } = useChargebeePortals();
  const myPlanSlug = activeSubscription?.planSlug;
  const isAnnual = isLegacyAnnualPlan(myPlanSlug);
  const isUpdating = useMemo(() => {
    return !!activeSubscription?.lastRenewalAttempt ||
      (!!user && !!activeSubscription && user.planSlug !== activeSubscription.planSlug);
    },
    [ user, activeSubscription ]
  );

  const { inProgress: apiCallInProgress } = useApiCalls();

  const [ checkingBillingInfoFor, setCheckingBillingInfoFor ] = useState<"manage_coupons" | undefined>(undefined);

  useEffect(() => {
      if (!apiCallInProgress) {
        setCheckingBillingInfoFor(undefined);
      }
    },
    [ apiCallInProgress, setCheckingBillingInfoFor ]
  );

  useEffect(() => {
      if (checkingBillingInfoFor) {
        setApiCallInProgress?.();
      }
    },
    [ checkingBillingInfoFor, setApiCallInProgress ]
  );

  const SubscriptionStatus = useCallback(
    ({
       isUpdating = false,
       has_scheduled_changes,
       scheduledSubscription,
       current_term_end,
       next_renewal_at,
       auto_renewal,
       has_payment_issues
     }: {
      isUpdating?: boolean;
      has_scheduled_changes: boolean;
      scheduledSubscription: ActiveSubscription["scheduledSubscription"];
      current_term_end: ActiveSubscription["currentTermEnd"];
      next_renewal_at: ActiveSubscription["nextRenewalAt"];
      auto_renewal: number | undefined;
      has_payment_issues: boolean;
    }) => {
      if (isUpdating) {
        return <>Your billing information is being updated.</>;
      }

      const addAutoRenewalAndPaymentIssuesMessages = (statusMessageParts: ReactElement[], auto_renewal: number | undefined, has_payment_issues: boolean) => {
        if (auto_renewal) {
          if (has_payment_issues) {
            statusMessageParts.push(<span key="payment issues">. The change will <b>not</b> happen automatically after reaching {auto_renewal}% of your credits because you have outstanding invoices.<br/></span>);
          } else {
            statusMessageParts.push(<span key="auto renewal"> or after reaching {auto_renewal}% of your credits.<br/></span>);
          }
        } else {
          statusMessageParts.push(<span key="no auto renewal">.<br/></span>);
        }
      };


      let statusMessageParts = [];

      const lastMonthOfAnnual = isAnnual && (next_renewal_at === current_term_end);

      if (isAnnual) {

        statusMessageParts.push(<span key="annual reset">Your credits will be reset</span>);

        if (lastMonthOfAnnual) {
          statusMessageParts.push(<span key="annual last month"> and </span>);
        } else {
          statusMessageParts.push(<span key="annual reset date"> on {fmtDate(new Date(next_renewal_at * 1000))}</span>);
          addAutoRenewalAndPaymentIssuesMessages(statusMessageParts, auto_renewal, has_payment_issues);
        }
      }

      const subscription = isAnnual ? (lastMonthOfAnnual ? "your annual" : "Your annual") : "Your";

      if (has_scheduled_changes) {
        if (scheduledSubscription?.planSlug === "free") {
          statusMessageParts.push(<span key="cancellation">{subscription} subscription will {lastMonthOfAnnual ? "also" : ""} be cancelled on </span>);
        } else {
          statusMessageParts.push(<span key="scheduled change">{subscription} subscription is scheduled to change to the {scheduledSubscription?.planName} plan on </span>);
        }
      } else {
        statusMessageParts.push(<span key="annual renewal">{subscription} subscription will {lastMonthOfAnnual ? "also" : ""} be renewed on </span>);
      }
      statusMessageParts.push(<span key="renewal date">{fmtDate(new Date(current_term_end * 1000))}</span>);

      if (!isAnnual || lastMonthOfAnnual) {
        addAutoRenewalAndPaymentIssuesMessages(statusMessageParts, auto_renewal, has_payment_issues);
      } else {
        statusMessageParts.push(<span key="trailing dot">.</span>);
      }

      return <>{statusMessageParts}</>;
    },
    [ isAnnual ]
  );

  return (
    <div
      className="grid lg:grid-cols-lgSubscriptionCard px-12 py-6 bg-white border border-borderColor dark:border-neutral-200 gap-x-16 gap-y-8"
    >
      <div className="flex flex-col items-center md:flex-row gap-x-16">
        <ScraperAPIIllustration3 className="flex-shrink-0 h-40 rounded-md w-28"/>
        <div className="flex flex-col items-center justify-center space-y-2 text-center md:items-start md:text-left w-full">
          {user.isBlocked && (
            <span className="text-lg text-brandDarkest dark:text-primary-800">
              <TaggedText message="Account blocked. [Contact support|contact_support]."/>
            </span>
          )}
          {!user.isBlocked && (
            <>
              <span className="text-lg text-brandDarkest dark:text-primary-800">
                You are currently on the <b>{activeSubscription?.planName}</b> plan
              </span>
              <div className="text-sm text-gray dark:text-neutral-600">
                <>
                  You have used {fmtNumber(counters?.requestCount)} of {fmtNumber(activeSubscription?.apiCreditLimit)}{" "}
                  successful credits this month on the {activeSubscription?.planName} plan{ counters?.payAsYouGoCredits ? ", plus " + fmtNumber(counters.payAsYouGoCredits) + " Pay-as-you-go credits." : "." }
                  <br/>
                </>
                <SubscriptionStatus
                  isUpdating={isUpdating}
                  has_scheduled_changes={ !!activeSubscription?.scheduledSubscription }
                  scheduledSubscription={activeSubscription?.scheduledSubscription}
                  current_term_end={activeSubscription?.currentTermEnd ?? 0}
                  next_renewal_at={activeSubscription?.nextRenewalAt ?? 0}
                  auto_renewal={user.planAutoRenewal}
                  has_payment_issues={user.hasPaymentIssues}
                />
              </div>
              {/* Legacy pricing note (ENG-1112). The v1 page still serves legacy annual plans, which are pre-cut-off paid accounts. */}
              {isGrandfatheredPlan(user.newMultipliersEnabled, getPlanCategory(activeSubscription?.planSlug, user.isFreeTrial || false)) && (
                <LegacyPricingNote className="w-full text-left" />
              )}
              <PricingOptionsCard />
            </>
          )}
        </div>
      </div>
      <div
        className="flex flex-col justify-center items-center lg:items-start pt-8 lg:pt-0 lg:pl-12 text-xs lg:text-left whitespace-nowrap gap-4 border-borderColor dark:border-neutral-200 border-t lg:border-t-0 lg:border-l"
      >
        <StatefulLink
          onClick={() => openChargebeePortal("ADDRESS")}
          hoverClassName="hover:cursor-pointer hover:underline"
          disabled={(user.isBlocked && user.blockingCode !== "banned_from_free") || !isChargebeeReady}
        >
          Edit billing address
        </StatefulLink>
        <StatefulLink
          onClick={() => openChargebeePortal("PAYMENT_SOURCES")}
          hoverClassName="hover:cursor-pointer hover:underline"
          disabled={(user.isBlocked && user.blockingCode !== "banned_from_free") || !isChargebeeReady}
        >
          Edit payment method
        </StatefulLink>
        <StatefulLink
          onClick={() => openChargebeePortal("BILLING_HISTORY")}
          hoverClassName="hover:cursor-pointer hover:underline"
          disabled={user.isBlocked || !isChargebeeReady}
        >
          View billing history
        </StatefulLink>
        <StatefulLink
          onClick={() => openChargebeePortal("SUBSCRIPTION_DETAILS")}
          hoverClassName="hover:cursor-pointer hover:underline"
          disabled={user.isBlocked || !isChargebeeReady}
        >
          Manage subscription
        </StatefulLink>
        { activeSubscription?.capabilities?.coupons && (user.canUseAllCoupons || (user.canUseCoupons && !isAnnual)) && (
          <StatefulLink
            to="coupons"
            // onClick={() => setCheckingBillingInfoFor("manage_coupons")}
            addLocationToState
            className="flex gap-2"
            hoverClassName="hover:cursor-pointer hover:underline"
            disabled={user.hasPaymentIssues || user.isBlocked || apiCallInProgress}
          >
            Manage coupons
            {(checkingBillingInfoFor === "manage_coupons" && apiCallInProgress) && (
              <Spinner size="XXS"/>
            )}
          </StatefulLink>
        )}
        {activeSubscription?.planSlug && (activeSubscription?.planSlug !== "free") && (
          <CancelSubscriptionLinks />
        )}
        {activeSubscription?.planSlug && !isEnterprise(activeSubscription.planSlug) && (
          <StatefulLink
            to="delete-account"
            addLocationToState
            hoverClassName="hover:cursor-pointer hover:underline"
            disabled={user.hasPaymentIssues || user.isPendingDeletion || user.isBlocked || apiCallInProgress}
          >
            Request account deletion
          </StatefulLink>
        )}
      </div>
    </div>
  );
};
